import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { createCheckoutSession } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

// Initialize Stripe - Replace with your publishable key
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const TOKEN_PACKAGES = [
  { id: 'starter', tokens: 120, price: 1, bonus: '20%', popular: false },
  { id: 'popular', tokens: 700, price: 5, bonus: '40%', popular: true },
  { id: 'mega', tokens: 1600, price: 10, bonus: '60%', popular: false },
];

function BuyVotes({ session, onPurchaseComplete }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const handlePurchase = async (packageId) => {
    // Check if user is authenticated
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }

    setLoading(true);
    setSelectedPackage(packageId);

    try {
      const stripe = await stripePromise;

      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      // Create checkout session
      const { sessionId } = await createCheckoutSession(packageId);

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId,
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        alert('Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('Purchase error:', err);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
      setSelectedPackage(null);
    }
  };

  const handleLogin = () => {
    window.location.href = `${import.meta.env.VITE_API_URL || '/api'}/auth/login`;
  };

  return (
    <div className="buy-votes-container">
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="login-prompt-overlay" onClick={() => setShowLoginPrompt(false)}>
          <div className="login-prompt-modal" onClick={(e) => e.stopPropagation()}>
            <h2>🔒 Login Required</h2>
            <p>You need to create a Roblox account to purchase Lit-Bits!</p>
            <p className="login-benefit">
              With an account, your Lit-Bits are saved forever and accessible from any device.
            </p>
            <div className="login-prompt-buttons">
              <button className="login-btn" onClick={handleLogin}>
                Login with Roblox
              </button>
              <button className="cancel-btn" onClick={() => setShowLoginPrompt(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="buy-votes-header">
        <h1 className="buy-votes-title">
          <img src="/litbitcoin.png" alt="Lit-Bit" className="litbit-icon-title" /> Buy Lit-Bits <img src="/litbitcoin.png" alt="Lit-Bit" className="litbit-icon-title" />
        </h1>
        <p className="buy-votes-subtitle">
          Get more Lit-Bits to vote and farm aura!
        </p>
        <div className="current-votes">
          You have <span className="vote-count">{user ? (session?.tokens || 0) : '0'}</span> Lit-Bits
          {!user && <span className="login-hint"> (Login to purchase)</span>}
        </div>
      </div>

      <div className="packages-grid">
        {TOKEN_PACKAGES.map(pkg => (
          <div
            key={pkg.id}
            className={`package-card ${pkg.popular ? 'popular' : ''}`}
          >
            {pkg.popular && (
              <div className="popular-badge">⭐ Most Popular</div>
            )}

            <div className="package-header">
              <h3 className="package-votes">{pkg.tokens}</h3>
              <p className="package-label">Lit-Bits <img src="/litbitcoin.png" alt="Lit-Bit" className="litbit-icon-small" /></p>
              {pkg.bonus && (
                <div className="bonus-badge">{pkg.bonus} Bonus</div>
              )}
            </div>

            <div className="package-price">
              <span className="currency">$</span>
              <span className="amount">{pkg.price}</span>
            </div>

            <div className="package-value">
              <p>{(pkg.tokens / pkg.price).toFixed(0)} Lit-Bits per dollar</p>
            </div>

            <button
              className={`package-button ${pkg.popular ? 'popular' : ''}`}
              onClick={() => handlePurchase(pkg.id)}
              disabled={loading}
            >
              {loading && selectedPackage === pkg.id ? (
                <span className="button-loading">
                  <span className="spinner"></span>
                  Processing...
                </span>
              ) : (
                'Buy Now'
              )}
            </button>
          </div>
        ))}
      </div>

      <div className="buy-votes-info">
        <div style={{
          background: 'rgba(0, 191, 255, 0.1)',
          border: '2px solid #00BFFF',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>
          <p style={{marginBottom: '0.5rem', fontWeight: 'bold'}}>⚠️ IMPORTANT: Please Read Before Purchasing</p>
          <p style={{fontSize: '0.95rem'}}>
            Lit-Bits are stored using your browser's Session ID. Clearing browser data will cause permanent loss of all Lit-Bits (including purchased ones). All sales are final and non-refundable.
          </p>
          <a
            href="/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: '0.5rem',
              color: '#00FFFF',
              textDecoration: 'underline',
              fontWeight: 'bold'
            }}
          >
            Read Full Terms & Token System Explanation →
          </a>
        </div>

        <h3>🎁 Daily Free: 50 Lit-Bits (resets midnight UTC)</h3>
        <h3>How to Spend Lit-Bits</h3>
        <ul>
          <li>⚡ Aura Farm Swipe: 10 Lit-Bits - random feed scrolling</li>
          <li>🎯 Targeted Vote: 5 Lit-Bits - pick category & Fire/L on specific avatar</li>
          <li>💪 Power Vote: 12 Lit-Bits - x3 impact for leaderboard wars (Coming Soon)</li>
          <li>🚀 Farm Boost: 20 Lit-Bits - double your next 20 swipes' Aura effect (Coming Soon)</li>
          <li>⭐ Spotlight: 30 Lit-Bits - top feed for 30 min (Coming Soon)</li>
        </ul>

        <div className="faq-section">
          <h3>FAQ</h3>
          <div className="faq-item">
            <p className="faq-question">Are Lit-Bits refundable?</p>
            <p className="faq-answer">
              No, all Lit-Bit purchases are final. Make sure to select the right package!
            </p>
          </div>
          <div className="faq-item">
            <p className="faq-question">Do Lit-Bits expire?</p>
            <p className="faq-answer">
              No! Your Lit-Bits remain in your browser session until you use them.
            </p>
          </div>
          <div className="faq-item">
            <p className="faq-question">What's the difference between Aura Swipe and Targeted Vote?</p>
            <p className="faq-answer">
              Aura Farm Swipe (10 Lit-Bits) is a random feed where you swipe Fire 🔥 or L on avatars. Targeted Vote (5 Lit-Bits) lets you pick a specific avatar and category to vote on - perfect for revenge or flexing!
            </p>
          </div>
          <div className="faq-item">
            <p className="faq-question">Is my payment secure?</p>
            <p className="faq-answer">
              Yes! We use Stripe for payment processing. We never see or store your payment information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BuyVotes;
